import { useEffect, useState } from 'react'
import type { PraesentationEinstellungen } from '@k4ia/shared'
import {
  deletePraesentation,
  deletePraesentationFolie,
  getPraesentation,
  importPraesentationSkript,
  praesentationBildUrl,
  praesentationTonVorbereiten,
  updatePraesentationEinstellungen,
  updatePraesentationFolie,
  uploadPraesentationBild,
  type AdminPraesentation,
  type AdminPraesentationFolie,
  type SkriptErgebnis,
} from '../lib/api'
import { FileButton } from '../components/ui'
import { verkleinereBild, BILD_NICHT_LESBAR } from '../lib/bild'

/** Solange die Präsentation läuft: wie oft die Seite den Stand nachliest. */
const STAND_POLL_MS = 3000

type Meldung = { art: 'ok' | 'fehler'; text: string }

/**
 * Präsentation auf der Bühne: K4ia führt durch einen Foliensatz und spricht
 * je Folie einen festen Text — nur das, nichts Eigenes. Hier kommen die
 * Folien aus der PDF (im Browser zu Bildern gerendert), das Skript der
 * Gründer (eine Zeile „Folie 12 – Titel“, darunter der Text) und die
 * Kurzfassung für die Quartalsfolien. Alles liegt nur in der Datenbank und
 * in R2 dieser Umgebung; der Abgleich nach Test fasst es nicht an.
 */
export function PraesentationPage() {
  const [daten, setDaten] = useState<AdminPraesentation | null>(null)
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  const [busy, setBusy] = useState(false)
  const [fortschritt, setFortschritt] = useState<{ was: string; n: number; von: number } | null>(
    null,
  )
  const [skript, setSkript] = useState('')
  const [skriptErgebnis, setSkriptErgebnis] = useState<SkriptErgebnis | null>(null)

  const laden = async () => setDaten(await getPraesentation())

  useEffect(() => {
    void laden().catch((e: unknown) => fehler(e))
  }, [])

  useEffect(() => {
    if (daten?.stand.phase === 'bereit') return
    const id = setInterval(() => void laden().catch(() => undefined), STAND_POLL_MS)
    return () => clearInterval(id)
  }, [daten?.stand.phase])

  function fehler(e: unknown) {
    setMeldung({ art: 'fehler', text: e instanceof Error ? e.message : String(e) })
  }

  async function tun(arbeit: () => Promise<unknown>, ok?: string) {
    setBusy(true)
    setMeldung(null)
    try {
      await arbeit()
      await laden()
      if (ok) setMeldung({ art: 'ok', text: ok })
    } catch (e) {
      fehler(e)
    } finally {
      setBusy(false)
    }
  }

  /** Die PDF: Seite für Seite rendern und als Folie 1, 2, 3 … hochladen. */
  async function pdfLaden(file: File) {
    setMeldung(null)
    setBusy(true)
    const uebersprungen: number[] = []
    try {
      // pdf.js erst jetzt laden: Es ist groß und wird nur hier gebraucht.
      const { rendereFolien, zaehleSeiten } = await import('../lib/pdfFolien')
      const von = await zaehleSeiten(file)
      setFortschritt({ was: 'Folien aus der PDF', n: 0, von })
      for await (const folie of rendereFolien(file)) {
        setFortschritt({ was: 'Folien aus der PDF', n: folie.nr, von })
        if ('fehler' in folie) {
          uebersprungen.push(folie.nr)
          continue
        }
        await uploadPraesentationBild(folie.nr, folie.file)
      }
      await laden()
      setMeldung({
        art: uebersprungen.length ? 'fehler' : 'ok',
        text: uebersprungen.length
          ? `${von - uebersprungen.length} von ${von} Folien übernommen; nicht lesbar: ${uebersprungen.join(', ')}.`
          : `${von} Folien aus „${file.name}“ übernommen.`,
      })
    } catch (e) {
      fehler(e)
    } finally {
      setFortschritt(null)
      setBusy(false)
    }
  }

  async function bildLaden(nr: number, file: File) {
    const { FOLIE_KANTE } = await import('../lib/pdfFolien')
    const klein = await verkleinereBild(file, FOLIE_KANTE)
    if (!klein) throw new Error(BILD_NICHT_LESBAR)
    await uploadPraesentationBild(nr, klein)
  }

  async function skriptEinlesen() {
    if (!skript.trim()) return
    setSkriptErgebnis(null)
    await tun(async () => {
      const ergebnis = await importPraesentationSkript(skript)
      setSkriptErgebnis(ergebnis)
      setSkript('')
    }, 'Skript übernommen.')
  }

  /** Jede Folie mit Text einmal sprechen lassen — die MP3 liegt danach in R2, der Abend wartet auf nichts. */
  async function tonVorbereiten() {
    if (!daten) return
    setMeldung(null)
    setBusy(true)
    const quellen: Array<number | 'kurzfassung'> = daten.folien
      .filter((f) => f.text.trim())
      .map((f) => f.nr)
    if (daten.einstellungen.kurzfassung.trim()) quellen.push('kurzfassung')
    const probleme: string[] = []
    try {
      for (const [i, q] of quellen.entries()) {
        setFortschritt({ was: 'Tonspuren', n: i + 1, von: quellen.length })
        try {
          await praesentationTonVorbereiten(q)
        } catch (e) {
          probleme.push(
            `${q === 'kurzfassung' ? 'Kurzfassung' : `Folie ${q}`}: ${e instanceof Error ? e.message : String(e)}`,
          )
          // Ohne Sprachausgabe scheitert jede — dann nicht 69-mal.
          if (/nicht konfiguriert/.test(String(e))) break
        }
      }
      setMeldung(
        probleme.length
          ? { art: 'fehler', text: probleme.slice(0, 3).join(' · ') }
          : { art: 'ok', text: `${quellen.length} Tonspuren liegen bereit.` },
      )
    } finally {
      setFortschritt(null)
      setBusy(false)
    }
  }

  if (!daten) {
    return (
      <div className="max-w-3xl space-y-8">
        <h2 className="page-title">Präsentation</h2>
        {meldung ? (
          <p role="alert" className="text-sm text-status-error">
            {meldung.text}
          </p>
        ) : (
          <p className="text-sm text-text-muted">Lädt …</p>
        )}
      </div>
    )
  }

  const { stand } = daten
  const laeuft = stand.phase !== 'bereit'

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h2 className="page-title">Präsentation</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          K4ia führt auf der Bühne durch den Foliensatz: Der Bildschirm zeigt die Folie, K4ia
          spricht den hinterlegten Text — wörtlich, nichts Eigenes —, am Handy klickt man weiter. An
          der ersten Quartalsfolie entscheidet das Handy: alle Quartalsfolien oder die Kurzfassung.
          Nach der letzten Folie beantwortet K4ia Fragen, nur aus den Folientexten. Der Inhalt ist
          vertraulich und bleibt in dieser Umgebung.
        </p>
      </div>

      {meldung && (
        <p
          role={meldung.art === 'fehler' ? 'alert' : 'status'}
          className={`text-sm ${meldung.art === 'fehler' ? 'text-status-error' : 'text-text-muted'}`}
        >
          {meldung.text}
        </p>
      )}
      {fortschritt && (
        <p className="text-sm text-text-muted" role="status">
          {fortschritt.was}: {fortschritt.n} von {fortschritt.von} …
        </p>
      )}

      {/* Stand und Adressen */}
      <section className="card divide-y divide-[var(--color-border)]">
        <div className="px-5 py-4">
          <h3 className="font-semibold">Bühne</h3>
          <p className="mt-1 text-xs text-text-muted">
            {laeuft
              ? `Läuft gerade: Folie ${stand.folie}${stand.phase === 'fragen' ? ', Fragerunde' : ''}${stand.bildschirm.spricht ? ', K4ia spricht' : ''}.`
              : 'Nichts läuft. Am Handy oben „Präsentation“ wählen, dann „Präsentation starten“.'}
            {!daten.sprachausgabe &&
              ' Sprachausgabe ist in dieser Umgebung nicht konfiguriert — die Folien erscheinen, K4ia bleibt stumm.'}
          </p>
        </div>
        <LinkZeile
          label="Bildschirm (Bühne)"
          hint="Auf dem Laptop am großen Screen öffnen und einmal hineinklicken. Der Schlüssel in der Adresse gibt dem Bildschirm die Folien — nur diese Adresse nehmen."
          url={daten.buehne.screenUrl}
        />
        <LinkZeile label="Handy (Steuerung)" hint="Nur fürs Team" url={daten.buehne.remoteUrl} />
      </section>

      {/* PDF */}
      <section className="card space-y-3 p-5">
        <div>
          <h3 className="font-semibold">Folien aus der PDF</h3>
          <p className="text-xs text-text-muted">
            Die Präsentation als PDF (aus PowerPoint: Datei → Exportieren). Jede Seite wird hier im
            Browser zu einem Bild und als Folie 1, 2, 3 … hochgeladen; vorhandene Bilder werden
            ersetzt, Texte bleiben.
          </p>
        </div>
        <FileButton
          label="PDF wählen"
          accept="application/pdf"
          disabled={busy}
          onFile={pdfLaden}
          hint={daten.folien.length ? `${daten.folien.length} Folien hinterlegt` : undefined}
        />
      </section>

      {/* Skript */}
      <section className="card space-y-3 p-5">
        <div>
          <h3 className="font-semibold">Skript einlesen</h3>
          <p className="text-xs text-text-muted">
            Das Moderationsskript als Text: je Folie eine Zeile „Folie 12 – Titel“, darunter, was
            K4ia spricht. „Folien 46–57 – …“ mit Aufzählung verteilt die Punkte auf den Bereich;
            „Kurzfassung, falls die Folien 46–57 übersprungen werden: …“ setzt Kurzfassung und
            Quartalsbereich. Titel und Texte werden je Folie ersetzt, Bilder bleiben.
          </p>
        </div>
        <textarea
          className="input min-h-[10rem] font-mono text-xs"
          value={skript}
          onChange={(e) => setSkript(e.target.value)}
          placeholder={'Folie 1 – Titel\nGuten Abend …\n\nFolie 2 – Agenda\n…'}
          aria-label="Skript"
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !skript.trim()}
            onClick={() => void skriptEinlesen()}
          >
            Skript übernehmen
          </button>
          {skriptErgebnis && (
            <span className="text-xs text-text-muted" data-testid="skript-ergebnis">
              {skriptErgebnis.folien} Folien ({skriptErgebnis.von}–{skriptErgebnis.bis})
              {skriptErgebnis.quartale
                ? `, Quartale ${skriptErgebnis.quartale.von}–${skriptErgebnis.quartale.bis}`
                : ''}
              {skriptErgebnis.kurzfassung ? ', Kurzfassung' : ', keine Kurzfassung'}
              {skriptErgebnis.verworfen.length
                ? ` — nicht zugeordnet: ${skriptErgebnis.verworfen
                    .slice(0, 3)
                    .map((z) => `„${z.slice(0, 40)}“`)
                    .join(', ')}`
                : ''}
            </span>
          )}
        </div>
      </section>

      <Einstellungen
        werte={daten.einstellungen}
        letzte={daten.folien.at(-1)?.nr ?? 0}
        busy={busy}
        onSpeichern={(w) => tun(() => updatePraesentationEinstellungen(w), 'Gespeichert.')}
      />

      {/* Folien */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-semibold">Folien</h3>
            <p className="text-xs text-text-muted">
              Bild, Titel und Sprechtext je Folie. Ohne Text wird die Folie nur gezeigt. Beträge so
              schreiben, wie K4ia sie sagen soll („Tausend Euro“, nicht „T€“).
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || !daten.sprachausgabe || daten.folien.length === 0}
            onClick={() => void tonVorbereiten()}
            title="Jede Folie einmal vertonen, damit am Abend nichts wartet"
          >
            Ton vorbereiten
          </button>
        </div>
        {daten.folien.length === 0 && (
          <p className="card p-5 text-sm text-text-muted">
            Noch keine Folie — PDF oder Skript laden.
          </p>
        )}
        <ol className="space-y-3">
          {daten.folien.map((f) => (
            <FolieKarte
              key={f.nr}
              folie={f}
              quartal={
                daten.einstellungen.quartaleVon !== null &&
                daten.einstellungen.quartaleBis !== null &&
                f.nr >= daten.einstellungen.quartaleVon &&
                f.nr <= daten.einstellungen.quartaleBis
              }
              busy={busy}
              onSpeichern={(w) => tun(() => updatePraesentationFolie(f.nr, w), 'Gespeichert.')}
              onBild={(file) => tun(() => bildLaden(f.nr, file))}
              onLoeschen={() => {
                if (window.confirm(`Folie ${f.nr} mit Bild und Text löschen?`))
                  void tun(() => deletePraesentationFolie(f.nr))
              }}
            />
          ))}
        </ol>
      </section>

      {daten.folien.length > 0 && (
        <section className="card flex flex-col gap-2 p-5">
          <h3 className="font-semibold">Alles löschen</h3>
          <p className="text-xs text-text-muted">
            Folien, Bilder, Texte, Kurzfassung — und der Bildschirm geht auf Anfang.
          </p>
          <button
            type="button"
            className="btn-danger self-start"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Wirklich alle Folien, Bilder und Texte löschen?'))
                void tun(() => deletePraesentation(), 'Alles gelöscht.')
            }}
          >
            Präsentation löschen
          </button>
        </section>
      )}
    </div>
  )
}

function Einstellungen({
  werte,
  letzte,
  busy,
  onSpeichern,
}: {
  werte: PraesentationEinstellungen
  letzte: number
  busy: boolean
  onSpeichern: (w: Partial<PraesentationEinstellungen>) => Promise<void>
}) {
  const [von, setVon] = useState(werte.quartaleVon?.toString() ?? '')
  const [bis, setBis] = useState(werte.quartaleBis?.toString() ?? '')
  const [ansprechpartner, setAnsprechpartner] = useState(werte.ansprechpartner)
  const [kurzfassung, setKurzfassung] = useState(werte.kurzfassung)
  const [autoPause, setAutoPause] = useState(String(werte.autoPauseSekunden))
  const [autoStumm, setAutoStumm] = useState(String(werte.autoStummSekunden))

  useEffect(() => {
    setVon(werte.quartaleVon?.toString() ?? '')
    setBis(werte.quartaleBis?.toString() ?? '')
    setAnsprechpartner(werte.ansprechpartner)
    setKurzfassung(werte.kurzfassung)
    setAutoPause(String(werte.autoPauseSekunden))
    setAutoStumm(String(werte.autoStummSekunden))
  }, [werte])

  const zahl = (s: string) => (s.trim() === '' ? null : Number(s))

  return (
    <section className="card space-y-3 p-5">
      <div>
        <h3 className="font-semibold">Quartalsfolien und Kurzfassung</h3>
        <p className="text-xs text-text-muted">
          An der ersten Folie dieses Bereichs hält K4ia an; das Handy wählt „Quartale zeigen“ oder
          „Kurzfassung“. Die Kurzfassung spricht sie dann statt der Folien {von || '…'}–{bis || '…'}
          {letzte ? ` (der Satz hat ${letzte} Folien)` : ''}.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-sm">
          Erste Quartalsfolie
          <input
            className="input w-28"
            type="number"
            min={1}
            value={von}
            onChange={(e) => setVon(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Letzte Quartalsfolie
          <input
            className="input w-28"
            type="number"
            min={1}
            value={bis}
            onChange={(e) => setBis(e.target.value)}
          />
        </label>
        <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm">
          Ansprechpartner für Verbindliches
          <input
            className="input"
            value={ansprechpartner}
            onChange={(e) => setAnsprechpartner(e.target.value)}
            placeholder="Tobias Wahner und Ulrich Gauweiler"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        Kurzfassung
        <textarea
          className="input min-h-[6rem]"
          value={kurzfassung}
          onChange={(e) => setKurzfassung(e.target.value)}
        />
      </label>
      <div className="space-y-2">
        <div>
          <h4 className="text-sm font-medium">Automatik</h4>
          <p className="text-xs text-text-muted">
            Am Handy einschaltbar: K4ia klickt nach jeder Folie selbst weiter, hält aber an der
            ersten Quartalsfolie und vor der Fragerunde an. Pause, Zurück und Springen schalten sie
            aus.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-sm">
            Pause nach der Tonspur (Sekunden)
            <input
              className="input w-28"
              type="number"
              min={0}
              max={30}
              step={0.5}
              value={autoPause}
              onChange={(e) => setAutoPause(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Folie ohne Text bleibt (Sekunden)
            <input
              className="input w-28"
              type="number"
              min={0}
              max={60}
              step={0.5}
              value={autoStumm}
              onChange={(e) => setAutoStumm(e.target.value)}
            />
          </label>
        </div>
      </div>
      <button
        type="button"
        className="btn-primary self-start"
        disabled={busy}
        onClick={() =>
          void onSpeichern({
            quartaleVon: zahl(von),
            quartaleBis: zahl(bis),
            ansprechpartner: ansprechpartner.trim(),
            kurzfassung,
            autoPauseSekunden: Number(autoPause) >= 0 ? Number(autoPause) : 2,
            autoStummSekunden: Number(autoStumm) >= 0 ? Number(autoStumm) : 4,
          })
        }
      >
        Speichern
      </button>
    </section>
  )
}

function FolieKarte({
  folie,
  quartal,
  busy,
  onSpeichern,
  onBild,
  onLoeschen,
}: {
  folie: AdminPraesentationFolie
  quartal: boolean
  busy: boolean
  onSpeichern: (w: { titel: string; text: string }) => Promise<void>
  onBild: (file: File) => Promise<void>
  onLoeschen: () => void
}) {
  const [titel, setTitel] = useState(folie.titel)
  const [text, setText] = useState(folie.text)
  useEffect(() => {
    setTitel(folie.titel)
    setText(folie.text)
  }, [folie.titel, folie.text])
  const geaendert = titel !== folie.titel || text !== folie.text

  return (
    <li className="card flex flex-col gap-3 p-4 sm:flex-row" data-testid="admin-folie">
      <div className="flex w-full shrink-0 flex-col gap-2 sm:w-40">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          Folie {folie.nr}
          {quartal ? ' · Quartal' : ''}
        </span>
        {folie.bild ? (
          <img
            src={praesentationBildUrl(folie.nr, folie.bild)}
            alt=""
            className="aspect-video w-full rounded-lg border border-border object-cover"
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-dashed border-border text-xs text-text-muted">
            Kein Bild
          </div>
        )}
        <FileButton
          label={folie.bild ? 'Bild ersetzen' : 'Bild hochladen'}
          accept="image/*"
          disabled={busy}
          onFile={onBild}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <input
          className="input"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="Titel"
          aria-label={`Titel Folie ${folie.nr}`}
        />
        <textarea
          className="input min-h-[5rem]"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Was K4ia zu dieser Folie spricht — leer: nur zeigen"
          aria-label={`Sprechtext Folie ${folie.nr}`}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !geaendert}
            onClick={() => void onSpeichern({ titel, text })}
          >
            Speichern
          </button>
          <button type="button" className="btn-chip ml-auto" disabled={busy} onClick={onLoeschen}>
            Löschen
          </button>
        </div>
      </div>
    </li>
  )
}

function LinkZeile({ label, hint, url }: { label: string; hint: string; url: string }) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4">
      <span className="text-[0.95rem] font-medium text-text">{label}</span>
      <span className="text-xs text-text-muted">{hint}</span>
      <span className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all font-mono text-sm">{url}</code>
        <button
          type="button"
          className="btn-chip shrink-0"
          onClick={() => void navigator.clipboard?.writeText(url)}
        >
          Kopieren
        </button>
      </span>
    </div>
  )
}
